import React, { lazy, Suspense } from 'react';
import { useCan, usePermissions, useResourceParams } from '@refinedev/core';
import { Flex, Spin } from 'antd';

type RouteAccessGuardProps = {
  children: React.ReactNode;
};

const PageNotFound = lazy(() =>
  import('@admin-panel/pages/PageNotFound').then(module => ({
    default: module.PageNotFound,
  })),
);

const RouteAccessGuard = ({ children }: RouteAccessGuardProps) => {
  const { resource, action } = useResourceParams();
  const { data: roles } = usePermissions({});

  const resourceName = resource?.name;
  const resolvedAction = action ?? 'list';
  const shouldCheck = Boolean(resourceName);

  const { data, isFetching } = useCan({
    resource: resourceName,
    action: resolvedAction,
    params: { resource },
    queryOptions: {
      enabled: shouldCheck && roles.length > 0,
    },
  });

  if (!shouldCheck) {
    return children;
  }

  const isInitialLoading = isFetching || !data || roles.length === 0;

  if (isInitialLoading) {
    return (
      <Flex
        style={{
          height: '100%',
        }}
        align="center"
        justify="center"
      >
        <Spin size="large" />
      </Flex>
    );
  }

  if (data?.can === false) {
    return (
      <Suspense fallback={<RouteGuardSpinner />}>
        <PageNotFound reason="unauthorized" />
      </Suspense>
    );
  }

  return children;
};

const RouteGuardSpinner = () => (
  <Flex
    style={{
      height: '100%',
    }}
    align="center"
    justify="center"
  >
    <Spin size="large" />
  </Flex>
);

export default RouteAccessGuard;
